import styled from '@emotion/styled';
import { Grid, Typography } from '@material-ui/core';
import { debounce } from 'lodash-es';
import { createElement, FC, Fragment, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMount } from 'react-use';
import { useRecoilValue } from 'recoil';

import { useGetIsPC } from '../../../../../utilityHooks/useGetIsPC';
import CreativeBriefEditor from '../../../../component/compound/CreativeBriefEditor';
import InputtedProjectPointTotal from '../../../../component/compound/InputtedProjectPointTotal';
import Spinner from '../../../../component/compound/Spinner';
import RegionSpecificFormattedMessage from '../../../../component/primitive/RegionSpecificFormattedMessage';
import { SCREEN_LARGE } from '../../../../constant/pageWidth';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../../../constant/spacing';
import { context as DependencyContext } from '../../../../context/dependency';
import { context as StateContext } from '../../../../context/state';
import mq from '../../../../styleUtility/mediaquery';
import {
  editingProjectAssetFilesStateAtom,
  editingProjectStateAtom,
  editingProjectStateErrorsAtom,
} from '../../atoms/editingProjectStateAtom';
import { isProjectFlatAtom } from '../../atoms/isProjectFlatAtom';
import { projectAtom, projectLoadingAtom } from '../../atoms/projectAtom';
import { getRegion } from '../../domain/helperFunctions';
import { hasAnyErrors } from '../../domain/stateValidator';
import { useEditingProjectState } from '../../hooks/useEditingProjectState';
import useUpdateProject from '../../hooks/useUpdateProject';
import { projectHasOptionsSelector } from '../../selectors/projectSelectors';
import LeftWorkflowStepper from '../LeftWorkflowStepper';
import MobileOnlyWorkflowStepper from '../MobileOnlyWorkflowStepper';
import AssetFormErrorMessages from './AssetFormErrorMessages';
import CreativeBriefActions from './CreativeBriefActions';
import CreativeBriefAssetFilesBlock from './CreativeBriefAssetFilesBlock';
import CreativeBriefProjectNameChip from './CreativeBriefProjectNameChip';

type Props = {
  setConfirmAlert: (state: boolean) => void;
};

const CreativeBriefScreen: FC<Props> = ({ setConfirmAlert }) => {
  const isPC = useGetIsPC();
  const isFlat = useRecoilValue(isProjectFlatAtom);
  const project = useRecoilValue(projectAtom);
  const isLoading = useRecoilValue(projectLoadingAtom);
  const state = useRecoilValue(editingProjectStateAtom);
  const errors = useRecoilValue(editingProjectStateErrorsAtom);
  const hasOption = useRecoilValue(projectHasOptionsSelector);
  const { assetFiles } = useRecoilValue(editingProjectAssetFilesStateAtom);
  const { deleteUnnecessaryAssetFiles, updateAssetCategoryValue } = useEditingProjectState();
  const { lastAccessedOrganization } = useContext(StateContext);
  const { updateDraftProjectAndGoToDifferentTab, updateDraftProjectAndGoToProjectsList } = useUpdateProject();
  const {
    editProjectUsecase: { setAssetFormTermAgreements: setAssetFormTermAgreements },
  } = useContext(DependencyContext);

  const region = getRegion(lastAccessedOrganization);

  const onClickSaveAsDraftButton = async () => {
    setConfirmAlert(false);
    await deleteUnnecessaryAssetFiles(project, assetFiles);
    await updateDraftProjectAndGoToProjectsList({ state, currentStep: 'brief' });
  };

  const onClickNextButton = async () => {
    setConfirmAlert(false);
    await deleteUnnecessaryAssetFiles(project, assetFiles);
    await updateDraftProjectAndGoToDifferentTab({
      state,
      currentStep: 'brief',
      targetStep: hasOption ? 'option' : 'preview',
    });
  };

  const onClickBackButton = async () => {
    setConfirmAlert(false);
    await deleteUnnecessaryAssetFiles(project, assetFiles);
    await updateDraftProjectAndGoToDifferentTab({
      state,
      currentStep: 'brief',
      targetStep: 'due',
    });
  };

  useMount(async () => {
    await setAssetFormTermAgreements();
  });

  if (!isFlat && (isLoading || !state)) return <Spinner fullHeight />;

  return (
    <Root isFlat={isFlat}>
      <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={isPC ? 4 : 2}>
        {/* conditionally show LeftWorkflowStepper if not flat */}
        {!isFlat ? (
          <LeftWorkflowStepper
            step="brief"
            updateDraftAndGoToTargetStep={(targetStep) =>
              updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'brief', targetStep })
            }
          />
        ) : (
          <Grid item xs={12} sm={12} md={12} lg={3} />
        )}
        <Grid item xs={12} sm={12} md={12} lg={9}>
          {isLoading || !state.creativeBriefValue || project?.projectType === null || !errors ? (
            <Spinner />
          ) : (
            <>
              {/* Inputted Project Total Point */}
              {!isFlat && <InputtedProjectPointTotal step="brief" />}

              {/* Creative Brief */}
              <Heading variant="h5">
                <RegionSpecificFormattedMessage
                  region={region}
                  messageId={'project_editing_page.creative_brief.description.main'}
                />
              </Heading>
              <SupportingText variant="body2" color="textSecondary">
                <FormattedMessage id="project_editing_page.creative_brief.description.sub" />
              </SupportingText>
              <CreativeBriefProjectNameChip title={state.title} />
              <CreativeBriefEditor project={project} />

              {/* Asset Files */}
              {state.assetCategoryValue && project?.bucketId && (
                <CreativeBriefAssetFilesBlock
                  bucketId={project.bucketId}
                  assetCategoryValue={state.assetCategoryValue}
                  onChangeAssetCategoryValue={debounce((_, value) => updateAssetCategoryValue(value), 250)}
                />
              )}
            </>
          )}
        </Grid>
      </Grid>

      <MobileOnlyWorkflowStepper
        step="brief"
        updateDraftAndGoToTargetStep={(targetStep) =>
          updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'brief', targetStep })
        }
      />

      {!isLoading && project?.creativeBriefDefinition && state.creativeBriefValue && errors && (
        <>
          <AssetFormErrorMessages errors={errors} />
          <CreativeBriefActions
            onClickBackButton={onClickBackButton}
            onClickSaveAsDraftButton={onClickSaveAsDraftButton}
            onClickNextButton={onClickNextButton}
            isStateValid={!hasAnyErrors(errors)}
            isFlat={isFlat}
            hasOption={hasOption}
          />
        </>
      )}
    </Root>
  );
};

const Root = styled.section<{ isFlat: boolean }>`
  width: auto;
  max-width: ${SCREEN_LARGE}px;
  margin: ${(props) => (props.isFlat ? 0 : SPACING_HUGE)}px auto ${(props) => (props.isFlat ? 0 : SPACING_UNIT * 12)}px;
  padding-bottom: ${SPACING_UNIT * 12}px;

  ${mq('sp')} {
    margin-top: ${SPACING_REGULAR}px;
    padding-left: ${SPACING_REGULAR}px;
    padding-right: ${SPACING_REGULAR}px;
    padding-bottom: ${SPACING_UNIT * 6}px;
  }
`;

const Heading = styled(Typography)`
  margin-bottom: ${SPACING_UNIT}px;

  ${mq('sp')} {
    font-size: 1.3rem;
  }
`;

const SupportingText = styled(Typography)`
  margin-bottom: ${SPACING_REGULAR};
`;

export default CreativeBriefScreen;
